/**
 * Schema Store
 *
 * Zustand store for data schema management.
 * Handles CRUD operations for reusable schema definitions.
 *
 * INTEGRATED: Uses data-service schemas (port 8002) instead of workflow backend.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import {
  schemaApi,
  Schema as DataServiceSchema,
  SchemaField as DataServiceSchemaField,
  CreateSchemaInput,
  UpdateSchemaInput,
} from '@/services/api/schemaApi';

// Schema field types (workflow uses uppercase)
export type SchemaFieldType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'ARRAY' | 'OBJECT';

// Schema status (workflow uses uppercase)
export type SchemaStatus = 'DRAFT' | 'PUBLISHED' | 'DEPRECATED';

// Schema field constraints
export interface SchemaFieldConstraints {
  // NUMBER constraints
  min?: number;
  max?: number;
  precision?: number;
  // STRING constraints
  minLength?: number;
  maxLength?: number;
  enum?: string[];
}

// Schema field definition
export interface SchemaField {
  id: string;
  name: string;
  type: SchemaFieldType;
  required: boolean;
  description?: string;
  constraints?: SchemaFieldConstraints;
}

// Full schema definition
export interface SchemaDefinition {
  id?: string;
  _id?: string;
  name: string;
  displayName?: string;
  description?: string;
  fields: SchemaField[];
  status?: SchemaStatus;
  version?: number;
  org_id?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface SchemaState {
  // Data
  schemas: SchemaDefinition[];
  selectedSchema: SchemaDefinition | null;

  // Builder state
  isBuilderOpen: boolean;
  currentSchema: SchemaDefinition | null;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  schemasLoaded: boolean;
  error: string | null;

  // Actions
  fetchSchemas: () => Promise<void>;
  getSchema: (schemaId: string) => Promise<SchemaDefinition | null>;
  createSchema: (data: SchemaDefinition) => Promise<SchemaDefinition | null>;
  updateSchema: (schemaId: string, data: Partial<SchemaDefinition>) => Promise<SchemaDefinition | null>;
  deleteSchema: (schemaId: string) => Promise<boolean>;
  duplicateSchema: (schemaId: string, newName?: string) => Promise<SchemaDefinition | null>;
  publishSchema: (schemaId: string) => Promise<SchemaDefinition | null>;

  // Builder actions
  openBuilder: (schema?: SchemaDefinition) => void;
  closeBuilder: () => void;
  setCurrentSchema: (schema: SchemaDefinition | null) => void;
  updateCurrentSchema: (updates: Partial<SchemaDefinition>) => void;

  // Field actions
  addField: (field: SchemaField) => void;
  updateField: (fieldId: string, updates: Partial<SchemaField>) => void;
  deleteField: (fieldId: string) => void;
  reorderFields: (fromIndex: number, toIndex: number) => void;

  // Save
  saveCurrentSchema: () => Promise<SchemaDefinition | null>;
}

// Generate a unique field ID
const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ============================================================================
// Transformation Functions (Data Service <-> Workflow Format)
// ============================================================================

/**
 * Transform data-service schema to workflow-compatible format
 * Converts lowercase types to uppercase for workflow compatibility
 */
const transformToWorkflowFormat = (schema: DataServiceSchema): SchemaDefinition => ({
  id: schema.id,
  _id: schema.id,
  name: schema.name,
  displayName: schema.displayName,
  description: schema.description,
  fields: schema.fields.map((f: DataServiceSchemaField) => ({
    id: f.id,
    name: f.name,
    type: f.type.toUpperCase() as SchemaFieldType,
    required: f.required,
    description: f.description,
    constraints: f.constraints,
  })),
  status: schema.status.toUpperCase() as SchemaStatus,
  version: schema.currentVersion,
  org_id: schema.organizationId,
  createdAt: schema.createdAt,
  updatedAt: schema.updatedAt,
});

/**
 * Transform workflow schema to data-service input format
 * Converts uppercase types to lowercase for data-service API
 */
const transformToDataServiceInput = (data: SchemaDefinition): CreateSchemaInput => ({
  name: data.name,
  displayName: data.displayName || data.name,
  description: data.description || '',
  fields: data.fields.map((f) => ({
    id: f.id,
    name: f.name,
    type: f.type.toLowerCase() as 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object',
    required: f.required,
    description: f.description,
    constraints: f.constraints,
  })),
});

/**
 * Transform workflow schema updates to data-service update format
 */
const transformToDataServiceUpdate = (data: Partial<SchemaDefinition>): UpdateSchemaInput => {
  const update: UpdateSchemaInput = {};

  if (data.displayName !== undefined) {
    update.displayName = data.displayName;
  }

  if (data.description !== undefined) {
    update.description = data.description;
  }

  if (data.fields !== undefined) {
    update.fields = data.fields.map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type.toLowerCase() as 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object',
      required: f.required,
      description: f.description,
      constraints: f.constraints,
    }));
  }

  return update;
};

// Create a new empty schema
const createEmptySchema = (): SchemaDefinition => ({
  name: '',
  displayName: '',
  description: '',
  fields: [
    {
      id: generateFieldId(),
      name: '',
      type: 'STRING',
      required: false,
      description: '',
      constraints: {},
    },
  ],
  status: 'DRAFT',
});

export const useSchemaStore = create<SchemaState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      schemas: [],
      selectedSchema: null,
      isBuilderOpen: false,
      currentSchema: null,
      isLoading: false,
      isSaving: false,
      schemasLoaded: false,
      error: null,

      // Fetch all schemas from data-service
      fetchSchemas: async () => {
        const { isLoading, schemasLoaded } = get();

        // Guard: Don't fetch if already loading or already loaded
        if (isLoading || schemasLoaded) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const result = await schemaApi.listSchemas();
          const schemas = result.data.map(transformToWorkflowFormat);

          set({
            schemas,
            isLoading: false,
            schemasLoaded: true,
          });
        } catch (error) {
          console.error('Failed to fetch schemas:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch schemas',
            isLoading: false,
          });
        }
      },

      // Get single schema from data-service
      getSchema: async (schemaId: string) => {
        try {
          const schema = await schemaApi.getSchema(schemaId);
          return transformToWorkflowFormat(schema);
        } catch (error) {
          console.error('Failed to get schema:', error);
          return null;
        }
      },

      // Create new schema in data-service
      createSchema: async (data: SchemaDefinition) => {
        set({ isSaving: true, error: null });

        try {
          const input = transformToDataServiceInput(data);
          const schema = await schemaApi.createSchema(input);
          const newSchema = transformToWorkflowFormat(schema);

          set((state) => {
            state.schemas.push(newSchema);
            state.isSaving = false;
          });

          return newSchema;
        } catch (error) {
          console.error('Failed to create schema:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to create schema',
            isSaving: false,
          });
          return null;
        }
      },

      // Update schema in data-service
      updateSchema: async (schemaId: string, data: Partial<SchemaDefinition>) => {
        set({ isSaving: true, error: null });

        try {
          const input = transformToDataServiceUpdate(data);
          const schema = await schemaApi.updateSchema(schemaId, input);
          const updatedSchema = transformToWorkflowFormat(schema);

          set((state) => {
            const index = state.schemas.findIndex((s) => s.id === schemaId);
            if (index !== -1) {
              state.schemas[index] = updatedSchema;
            }
            state.isSaving = false;
          });

          return updatedSchema;
        } catch (error) {
          console.error('Failed to update schema:', error);
          set({ isSaving: false });
          return null;
        }
      },

      // Delete schema from data-service
      deleteSchema: async (schemaId: string) => {
        try {
          await schemaApi.deleteSchema(schemaId);

          set((state) => {
            state.schemas = state.schemas.filter((s) => s.id !== schemaId);
          });

          return true;
        } catch (error) {
          console.error('Failed to delete schema:', error);
          return false;
        }
      },

      // Duplicate schema using data-service fork
      duplicateSchema: async (schemaId: string, newName?: string) => {
        try {
          const schema = await schemaApi.forkSchema(schemaId, newName);
          const duplicatedSchema = transformToWorkflowFormat(schema);

          set((state) => {
            state.schemas.push(duplicatedSchema);
          });

          return duplicatedSchema;
        } catch (error) {
          console.error('Failed to duplicate schema:', error);
          return null;
        }
      },

      // Publish schema in data-service
      publishSchema: async (schemaId: string) => {
        try {
          const schema = await schemaApi.publishSchema(schemaId);
          const publishedSchema = transformToWorkflowFormat(schema);

          set((state) => {
            const index = state.schemas.findIndex((s) => s.id === schemaId);
            if (index !== -1) {
              state.schemas[index] = publishedSchema;
            }
          });

          return publishedSchema;
        } catch (error) {
          console.error('Failed to publish schema:', error);
          return null;
        }
      },

      // Builder actions
      openBuilder: (schema?: SchemaDefinition) => {
        set({
          isBuilderOpen: true,
          currentSchema: schema ? { ...schema } : createEmptySchema(),
        });
      },

      closeBuilder: () => {
        set({
          isBuilderOpen: false,
          currentSchema: null,
        });
      },

      setCurrentSchema: (schema: SchemaDefinition | null) => {
        set({ currentSchema: schema });
      },

      updateCurrentSchema: (updates: Partial<SchemaDefinition>) => {
        set((state) => {
          if (state.currentSchema) {
            Object.assign(state.currentSchema, updates);
          }
        });
      },

      // Field actions
      addField: (field: SchemaField) => {
        set((state) => {
          if (state.currentSchema) {
            state.currentSchema.fields.push(field);
          }
        });
      },

      updateField: (fieldId: string, updates: Partial<SchemaField>) => {
        set((state) => {
          if (state.currentSchema) {
            const fieldIndex = state.currentSchema.fields.findIndex((f) => f.id === fieldId);
            if (fieldIndex !== -1) {
              const field = state.currentSchema.fields[fieldIndex];
              state.currentSchema.fields[fieldIndex] = { ...field, ...updates };
            }
          }
        });
      },

      deleteField: (fieldId: string) => {
        set((state) => {
          if (state.currentSchema) {
            state.currentSchema.fields = state.currentSchema.fields.filter((f) => f.id !== fieldId);
          }
        });
      },

      reorderFields: (fromIndex: number, toIndex: number) => {
        set((state) => {
          if (state.currentSchema && state.currentSchema.fields.length > 1) {
            const fields = [...state.currentSchema.fields];
            const [removed] = fields.splice(fromIndex, 1);
            fields.splice(toIndex, 0, removed);
            state.currentSchema.fields = fields;
          }
        });
      },

      // Save current schema
      saveCurrentSchema: async () => {
        const { currentSchema } = get();
        if (!currentSchema) return null;

        if (currentSchema.id) {
          // Update existing
          return get().updateSchema(currentSchema.id, {
            name: currentSchema.name,
            displayName: currentSchema.displayName,
            description: currentSchema.description,
            fields: currentSchema.fields,
          });
        } else {
          // Create new
          const newSchema = await get().createSchema(currentSchema);
          if (newSchema) {
            set({ currentSchema: newSchema });
          }
          return newSchema;
        }
      },
    })),
    { name: 'schema-store' }
  )
);
